/**
 * 상담 이관용 data-store.json 을 "고객 온전" 단위로 청킹한다.
 *
 * 왜: 서버 apply/preview 는 파일을 통째로 받아 메모리 파싱(수백 MB 스파이크) + 전량 동기 처리해서
 * 90MB 업로드 시 게이트웨이 504 가 난다. 브라우저에서 파싱해 작은 부분 store 로 쪼개 올리면,
 * 서버는 매 요청 작은 subset 만 파싱/처리하고(멱등이라 안전) 90MB 가 서버에 오르지 않는다.
 *
 * 정확성: EndUser 집계(firstSeenAt/messageCount 등)는 고객 단위 $setOnInsert 라, 한 고객의 방/메시지가
 * 여러 청크에 걸치면 안 된다. 그래서 서버 splitStore/buildPlan 과 "동일한" 고객키(channel+channelUserId)로
 * 방을 묶어 한 고객을 한 청크에 몰아 넣는다.
 *
 * 환자 배치가 결과를 바꾼다: 서버 buildPlan 은 "어떤 방에도 안 붙은 환자"를 orphan 으로 보고 CDP 에
 * manual 고객으로 직접 넣는다. 그래서 환자를 전 청크에 복사하면, 자기 방이 다른 청크에 있는 환자가
 * 매 청크마다 orphan 으로 오판돼 방 있는 고객의 쌍둥이 manual 고객이 대량 생성된다.
 * → 환자는 자기 방이 실린 청크에만 싣고, 진짜 orphan(전체 store 기준 어떤 방에도 안 붙는 환자)은
 *   마지막 청크에 한 번만 싣는다.
 */

// 서버 CHANNEL_BY_ROOM_TYPE 와 동일해야 한다.
const CHANNEL_BY_ROOM_TYPE = {
  ikakao: "kakao",
  talktalk: "naver-talktalk",
  instagram: "instagram",
  line: "line",
  whatsapp: "whatsapp",
};

// 서버 channelUserIdOf 와 동일. 이 값이 이관 고객과 실시간 인입을 잇는 유일한 키다.
function channelUserIdOf(room) {
  switch (room && room.roomType) {
    case "ikakao":
      return (room.ikakaoInfo && room.ikakaoInfo.user_key) || null;
    case "talktalk":
      return (room.talktalkInfo && room.talktalkInfo.talktalkUserId) || null;
    case "instagram":
      return (room.instagramInfo && room.instagramInfo.senderID) || null;
    case "line":
      return (room.lineInfo && room.lineInfo.lineUserId) || null;
    case "whatsapp":
      return (room.whatsappInfo && room.whatsappInfo.userID) || null;
    default:
      return null;
  }
}

function roomIdOf(room) {
  return (room && (room.id || room._id)) || null;
}

// 서버 hserviceIdOf 와 동일. 목록 응답은 문자열, 상세 응답은 환자 문서가 펼쳐진 객체로 온다.
function hserviceIdOf(room) {
  const v = room && room.hserviceID;
  if (!v) return null;
  return typeof v === "string" ? v : v._id || null;
}

/**
 * store: { [entryKey]: { type: 'chatroom'|'message'|'patient', data } }
 * chunkSize: 청크당 고객(그룹) 수
 * 반환: 부분 store 객체 배열. 각 부분 = 그 청크 고객들의 방+메시지 전부 + 전체 환자.
 */
export function chunkCounselStore(store, chunkSize = 100) {
  const roomEntries = []; // [entryKey, room]
  const patientKeys = []; // entryKey[]
  const msgKeysByRoomId = new Map(); // chatRoomID -> entryKey[]
  const patientKeysByRoomId = new Map(); // recentChatRoom.id -> entryKey[]
  const patientKeyByPatientId = new Map(); // patientId -> entryKey (room.hserviceID 조인용)

  for (const key of Object.keys(store)) {
    const e = store[key];
    if (!e) continue;
    if (e.type === "chatroom") {
      roomEntries.push([key, e.data]);
    } else if (e.type === "patient") {
      patientKeys.push(key);
      // 서버 indexPatients 와 같은 두 조인 경로를 미리 뒤집어 둔다.
      const rc = e.data && e.data.recentChatRoom && e.data.recentChatRoom.id;
      if (rc) {
        const list = patientKeysByRoomId.get(rc);
        if (list) list.push(key);
        else patientKeysByRoomId.set(rc, [key]);
      }
      if (e.data && e.data.patientId) patientKeyByPatientId.set(e.data.patientId, key);
    } else if (e.type === "message") {
      const rid = e.data && e.data.chatRoomID;
      if (!rid) continue;
      const list = msgKeysByRoomId.get(rid);
      if (list) list.push(key);
      else msgKeysByRoomId.set(rid, [key]);
    }
  }

  // 방을 그룹으로 묶는다: 유효 방은 고객키로 병합, 무효(채널/식별자 없음, patient 앱 등)는 각자 한 그룹.
  // 무효 방도 서버가 동일하게 "제외"로 처리하도록 청크에 실어 보낸다(그 방의 메시지도 함께 따라간다).
  const groups = new Map(); // groupKey -> roomEntryKey[]
  let orphanSeq = 0;
  for (const [key, room] of roomEntries) {
    const channel = CHANNEL_BY_ROOM_TYPE[(room && room.roomType) || ""];
    const cuid = channelUserIdOf(room);
    const groupKey = channel && cuid ? channel + " " + cuid : "__orphan_" + orphanSeq++;
    const list = groups.get(groupKey);
    if (list) list.push(key);
    else groups.set(groupKey, [key]);
  }

  // 이 방에 붙을 수 있는 환자 후보(서버 buildPlan 의 두 조인 경로). 어느 쪽이 이길지는
  // 여기서 예측하지 않는다 — 아래에서 청크 자신에게 물어본다.
  const patientCandidatesForRoom = (room) => {
    const out = [];
    const rid = roomIdOf(room);
    if (rid) {
      const byRoom = patientKeysByRoomId.get(rid);
      if (byRoom) out.push(...byRoom);
    }
    const hid = hserviceIdOf(room);
    if (hid) {
      const byHservice = patientKeyByPatientId.get(hid);
      if (byHservice) out.push(byHservice);
    }
    return out;
  };

  /**
   * 이 부분 store 안에서 서버 buildPlan 이 실제로 소비할 환자 entryKey 집합.
   * 서버의 `byRoom ?? byHservice` 우선순위를 그대로 재현한다.
   *
   * 왜 예측이 아니라 검증인가: 같은 recentChatRoom.id 를 가진 환자가 여럿이거나 다른 방의 환자가
   * 우연히 이 방의 byRoom 을 차지하는 등, 전체 store 의 해소 결과가 청크 안에서 뒤집히는 경우가 있다.
   * 한 건이라도 뒤집히면 그 환자는 청크 안에서 "방 없는 환자"로 보여 쌍둥이 manual 고객이 된다.
   */
  const consumedPatientKeys = (subset) => {
    const byRoom = new Map();
    const byPatientId = new Map();
    const roomsIn = [];
    const msgCount = new Map();
    for (const k of Object.keys(subset)) {
      const e = subset[k];
      if (e.type === "chatroom") roomsIn.push([k, e.data]);
      else if (e.type === "patient") {
        const rc = e.data && e.data.recentChatRoom && e.data.recentChatRoom.id;
        if (rc) byRoom.set(rc, k);
        if (e.data && e.data.patientId) byPatientId.set(e.data.patientId, k);
      } else if (e.type === "message") {
        const rid = e.data && e.data.chatRoomID;
        if (rid) msgCount.set(rid, (msgCount.get(rid) || 0) + 1);
      }
    }
    const consumed = new Set();
    for (const [, room] of roomsIn) {
      const rid = roomIdOf(room);
      // 서버가 제외하는 방(전용 앱/식별자 없음/메시지 0건)은 환자를 소비하지 않는다.
      if (!rid || room.roomType === "patient") continue;
      if (!CHANNEL_BY_ROOM_TYPE[room.roomType || ""] || !channelUserIdOf(room)) continue;
      if (!msgCount.get(rid)) continue;
      const pk = byRoom.get(rid) || byPatientId.get(hserviceIdOf(room) || "");
      if (pk) consumed.add(pk);
    }
    return consumed;
  };

  const groupKeys = Array.from(groups.keys());
  const chunks = [];
  const coveredPatientKeys = new Set();
  for (let i = 0; i < groupKeys.length; i += chunkSize) {
    const slice = groupKeys.slice(i, i + chunkSize);
    const subset = {};
    const included = new Set();
    for (const gk of slice) {
      for (const roomKey of groups.get(gk)) {
        subset[roomKey] = store[roomKey];
        const room = store[roomKey].data;
        const rid = roomIdOf(room);
        if (rid) {
          const msgKeys = msgKeysByRoomId.get(rid);
          if (msgKeys) for (const mk of msgKeys) subset[mk] = store[mk];
        }
        for (const pk of patientCandidatesForRoom(room)) {
          subset[pk] = store[pk];
          included.add(pk);
        }
      }
    }

    // 이 청크가 실제로 안 쓰는 환자는 빼버린다. 남겨두면 서버가 orphan 으로 오판한다.
    // 안 쓰이는 환자를 지우는 것은 다른 방의 해소 결과를 바꾸지 않는다(소비된 환자만 조회에 걸리므로).
    const consumed = consumedPatientKeys(subset);
    for (const pk of included) {
      if (consumed.has(pk)) coveredPatientKeys.add(pk);
      else delete subset[pk];
    }
    chunks.push(subset);
  }

  // 어떤 청크에서도 안 쓰인 환자 = 진짜 orphan. 서버가 CDP manual 고객으로 넣는다.
  // 마지막 청크에만 실어 한 번만 처리되게 한다(방이 0개여도 청크 하나는 만들어야 넣을 수 있다).
  const orphanKeys = patientKeys.filter((pk) => !coveredPatientKeys.has(pk));
  if (orphanKeys.length > 0) {
    if (chunks.length === 0) chunks.push({});
    const last = chunks[chunks.length - 1];
    for (const pk of orphanKeys) last[pk] = store[pk];
  }
  return chunks;
}

/**
 * 청크별 결과(요약/적재 카운트)를 깊은 합산한다.
 * 숫자는 더하고, 배열은 이어붙이며, 객체(dict)는 키별로 재귀 합산한다.
 */
export function mergeDeep(a, b) {
  if (a === undefined || a === null) return b;
  if (b === undefined || b === null) return a;
  if (typeof a === "number" && typeof b === "number") return a + b;
  if (Array.isArray(a) && Array.isArray(b)) return a.concat(b);
  if (typeof a === "object" && typeof b === "object") {
    const out = Object.assign({}, a);
    for (const k of Object.keys(b)) out[k] = mergeDeep(a[k], b[k]);
    return out;
  }
  return b;
}
